/**
 * Refund / withdrawal request popup opened from the footer.
 *
 * <refund-request-opener data-modal="FooterRefundModal"><button>…</button></refund-request-opener>
 * <refund-request-modal id="FooterRefundModal" data-form-id="FooterRefundForm">…</refund-request-modal>
 *
 * The modal moves itself to <body> so the fixed overlay is not clipped by the
 * footer. After a submit Shopify reloads the page with `#<form id>` in the URL
 * and the form renders a `[data-refund-status]` message (success or error) —
 * the modal reopens by itself in that case so the visitor sees the result.
 */

if (!customElements.get('refund-request-modal')) {
  class RefundRequestModal extends HTMLElement {
    constructor() {
      super();
      this.dialog = this.querySelector('[role="dialog"]');
      this.openedBy = null;

      this.querySelectorAll('[data-refund-close]').forEach((button) => {
        button.addEventListener('click', () => this.hide());
      });
      this.addEventListener('keyup', (event) => {
        if ((event.code || '').toUpperCase() === 'ESCAPE') this.hide();
      });
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }

    connectedCallback() {
      if (this.moved) return;
      this.moved = true;

      // The theme editor re-renders the footer on every setting change; drop the
      // copy a previous render already parked on <body>.
      document.querySelectorAll('body > refund-request-modal').forEach((modal) => {
        if (modal !== this && modal.id === this.id) modal.remove();
      });
      document.body.appendChild(this);

      this.maybeAutoOpen();
    }

    maybeAutoOpen() {
      if (!this.querySelector('[data-refund-status]')) return;
      const formId = this.dataset.formId;
      const hash = window.location.hash;
      if (hash && hash !== `#${formId}`) return;
      this.show(null);
    }

    get motionDuration() {
      return window.Theme && Theme.config && Theme.config.motionReduced ? 0 : 0.25;
    }

    animate(keyframes) {
      if (!window.Theme || !Theme.motion || !this.dialog) return Promise.resolve();
      return Theme.motion.animate(this.dialog, keyframes, {
        duration: this.motionDuration,
        easing: [0.25, 0.1, 0.25, 1],
      }).finished;
    }

    show(opener) {
      if (this.hasAttribute('open')) return;
      this.openedBy = opener || null;
      document.body.classList.add('overflow-hidden');
      this.setAttribute('open', '');

      const focusTarget = this.querySelector('[data-refund-status]') || this.dialog || this;
      if (typeof trapFocus === 'function') {
        trapFocus(this, focusTarget);
      } else {
        focusTarget.focus();
      }

      this.animate({
        opacity: [0, 1],
        transform: ['translateY(16px)', 'translateY(0)'],
      });
    }

    async hide() {
      if (!this.hasAttribute('open')) return;
      await this.animate({
        opacity: [1, 0],
        transform: ['translateY(0)', 'translateY(16px)'],
      });
      this.removeAttribute('open');
      document.body.classList.remove('overflow-hidden');
      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(this.openedBy);
      } else if (this.openedBy) {
        this.openedBy.focus();
      }
      this.openedBy = null;
    }
  }

  customElements.define('refund-request-modal', RefundRequestModal);
}

if (!customElements.get('refund-request-opener')) {
  class RefundRequestOpener extends HTMLElement {
    constructor() {
      super();
      const button = this.querySelector('button');
      if (!button) return;
      button.addEventListener('click', () => {
        const modal = document.getElementById(this.dataset.modal);
        if (modal && typeof modal.show === 'function') modal.show(button);
      });
    }
  }

  customElements.define('refund-request-opener', RefundRequestOpener);
}
